import { z } from 'zod';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
    defineStoredJsonSemanticFieldValue,
} from '../stored-json-contract.types';
import type {
    ForensicOptionSearchRequest,
    ForensicOrganizationReference,
    ForensicReferenceOption,
    ForensicRoleAssignmentReference,
    ForensicSessionOption,
    ForensicUserReference,
    ListForensicReferenceOptionsPageResponse,
    ListForensicSessionOptionsPageResponse,
    ListRecentRoleActivityPageResponse,
    ListRecentUserActivityPageResponse,
    ListTransactionLogDisplayPageResponse,
    ListTransactionLogPageResponse,
    CaptureCredentialAuditBaselineResponse,
    CredentialHistoricalRevealAvailability,
    CredentialHistoricalRevealEventDetails,
    CredentialHistoricalRevealEventDetailsV1,
    CredentialHistoricalRevealFieldDescriptor,
    CredentialHistoricalRevealFailureReason,
    EmailAuthenticationAttemptLimitResetEventDetails,
    EmailAuthenticationAttemptLimitResetEventDetailsV1,
    EmailAuthenticationAttemptLimitResetFailureCode,
    RecentActivityOrganizationReference,
    RecentActivityRoleAssignmentReference,
    RecentActivityUserReference,
    RecentRoleActivityListItem,
    RecentRoleActivityOperation,
    RecentRoleActivitySearchRequest,
    RecentUserActivityListItem,
    RecentUserActivityOperation,
    RecentUserActivitySearchRequest,
    TransactionLogTableRowSnapshot,
    RevealHistoricalCredentialFieldRequest,
    RevealHistoricalCredentialFieldResponse,
    TransactionLogCredentialDefinitionEvidence,
    TransactionLogCredentialDefinitionEvidenceV2,
    TransactionLogCredentialProtectedPayload,
    TransactionLogCredentialProtectedPayloadV1,
    TransactionLogCredentialProtectedPayloadV2,
    TransactionLogCredentialHistoricalRevealFailedListItem,
    TransactionLogCredentialHistoricalRevealSucceededListItem,
    TransactionLogEmailAuthenticationAttemptLimitResetFailedListItem,
    TransactionLogEmailAuthenticationAttemptLimitResetSucceededListItem,
    TransactionLogDeleteListItem,
    TransactionLogImpersonationEndedListItem,
    TransactionLogImpersonationStartedListItem,
    TransactionLogDisplayItem,
    TransactionLogInsertListItem,
    TransactionLogListItem,
    TransactionLogLoginListItem,
    TransactionLogLogoutSuccessListItem,
    TransactionLogMfaSuccessListItem,
    TransactionLogOperation,
    TransactionLogRoleAssignmentActivatedListItem,
    TransactionLogRowDetails,
    TransactionLogSearchRequest,
    TransactionLogSort,
    TransactionLogSessionExpiredListItem,
    TransactionLogTableName,
    TransactionLogUpdateListItem,
} from '../transaction-log.types';
import { TransactionLogSortDirections, TransactionLogSortFields } from '../transaction-log.types';
import { TransactionLogOperations, TransactionLogTableNames } from '../transaction-log.types';
import {
    CredentialType,
    IpxCredentialDefinitionKind,
    Role,
} from '@pillar-portal/database/dist/enums';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { AuthorizationPaginationLimitQuerySchema } from './authorization.schemas';
import { JsonObjectSchema, NonEmptyStringSchema } from './common.schemas';
import {
    CredentialOwnerScopeSchema,
    SupportedCredentialSystemIdentifierSafeIntegerDomain,
    SupportedCredentialSystemIdSchema,
    StoredIpxCredentialFieldConfigMapParser,
    StoredIpxCredentialFieldConfigMapSchema,
} from './secret.schemas';
import {
    SearchFieldPredicatesSchema,
    PhysicalJsonStructurePredicatesSchema,
    SearchTermsSchema,
} from './omnisearch.schemas';
import {
    defineSafeIntegerDomain,
    normalizeStoredJsonSafeIntegerFieldsAtPath,
    normalizeStoredJsonSafeIntegerObjectFields,
} from '../stored-json-number';

const TransactionLogCredentialProtectedPayloadV1SafeIntegerDomain = defineSafeIntegerDomain({
    name: 'transaction-log-credential-protected-payload-v1.protected_payload_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: [1],
    },
});

const TransactionLogCredentialProtectedPayloadV2SafeIntegerDomain = defineSafeIntegerDomain({
    name: 'transaction-log-credential-protected-payload-v2.protected_payload_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: [2],
    },
});

const EmailAuthenticationAttemptLimitResetEventDetailsV1SafeIntegerDomain = defineSafeIntegerDomain(
    {
        name: 'email-authentication-attempt-limit-reset-event-details-v1.event_details_version',
        provenance: {
            kind: 'finite-integer-domain',
            values: [1],
        },
    }
);

export const TransactionLogTableNameSchema: z.ZodType<TransactionLogTableName> =
    z.enum(TransactionLogTableNames);

export const TransactionLogOperationSchema: z.ZodType<TransactionLogOperation> = z.enum([
    ...TransactionLogOperations,
]);

export const TransactionLogTableRowSnapshotSchema: z.ZodType<TransactionLogTableRowSnapshot> =
    JsonObjectSchema;

export const TransactionLogRowDetailsSchema: z.ZodType<TransactionLogRowDetails> = JsonObjectSchema;

export function normalizeTransactionLogTableRowSnapshotStorageV1(
    snapshot: TransactionLogTableRowSnapshot
): TransactionLogTableRowSnapshot {
    return snapshot;
}

export const TransactionLogTableRowSnapshotStorageContract = defineStoredJsonContract({
    contractName: 'TransactionLogTableRowSnapshot',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'TransactionLogTableRowSnapshotSchema',
            schema: TransactionLogTableRowSnapshotSchema,
            guards: [],
            normalize: normalizeTransactionLogTableRowSnapshotStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredTransactionLogTableRowSnapshotParser: z.ZodType<TransactionLogTableRowSnapshot> =
    buildStoredJsonValueParser(TransactionLogTableRowSnapshotStorageContract);

export const TransactionLogCredentialProtectedPayloadV1Schema: z.ZodType<TransactionLogCredentialProtectedPayloadV1> =
    z
        .object({
            protected_payload_version: z.literal(1),
            protected_payload_kind: z.literal('CREDENTIAL_DML_SNAPSHOT'),
            ciphertext_encoding: z.literal('BASE64'),
            ciphertext_base64: z
                .string()
                .min(1)
                .regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/),
        })
        .strict();

export const StoredTransactionLogCredentialProtectedPayloadV1Schema: z.ZodType<TransactionLogCredentialProtectedPayloadV1> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: TransactionLogCredentialProtectedPayloadV1SafeIntegerDomain,
                fields: ['protected_payload_version'],
            }),
        TransactionLogCredentialProtectedPayloadV1Schema
    );

export const TransactionLogCredentialDefinitionEvidenceV2Schema: z.ZodType<TransactionLogCredentialDefinitionEvidenceV2> =
    z
        .object({
            uuid: z.string().uuid(),
            ipx_system_id: SupportedCredentialSystemIdSchema,
            credential_definition_key: z.string(),
            credential_definition_kind: z.enum(IpxCredentialDefinitionKind),
            credential_type: z.enum(CredentialType),
            credential_owner_scope: CredentialOwnerScopeSchema,
            credential_field_config: StoredIpxCredentialFieldConfigMapSchema,
        })
        .strict();

export const StoredTransactionLogCredentialDefinitionEvidenceV2Schema: z.ZodType<TransactionLogCredentialDefinitionEvidenceV2> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: SupportedCredentialSystemIdentifierSafeIntegerDomain,
                fields: ['ipx_system_id'],
            }),
        TransactionLogCredentialDefinitionEvidenceV2Schema
    );

export const TransactionLogCredentialProtectedPayloadV2Schema: z.ZodType<TransactionLogCredentialProtectedPayloadV2> =
    z
        .object({
            protected_payload_version: z.literal(2),
            protected_payload_kind: z.literal('CREDENTIAL_DML_SNAPSHOT'),
            ciphertext_encoding: z.literal('BASE64'),
            ciphertext_base64: z
                .string()
                .min(1)
                .regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/),
            credential_definition: TransactionLogCredentialDefinitionEvidenceV2Schema,
        })
        .strict();

export const StoredTransactionLogCredentialProtectedPayloadV2Schema: z.ZodType<TransactionLogCredentialProtectedPayloadV2> =
    z.preprocess((value: unknown): unknown => {
        const normalizedRoot = normalizeStoredJsonSafeIntegerObjectFields(value, {
            safeIntegerDomain: TransactionLogCredentialProtectedPayloadV2SafeIntegerDomain,
            fields: ['protected_payload_version'],
        });
        return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedRoot, {
            safeIntegerDomain: SupportedCredentialSystemIdentifierSafeIntegerDomain,
            path: ['credential_definition'],
            fields: ['ipx_system_id'],
        });
    }, TransactionLogCredentialProtectedPayloadV2Schema);

export function normalizeTransactionLogCredentialProtectedPayloadV1(
    payload: TransactionLogCredentialProtectedPayloadV1
): TransactionLogCredentialProtectedPayload {
    return {
        ciphertext_encoding: payload.ciphertext_encoding,
        ciphertext_base64: payload.ciphertext_base64,
        credential_definition: null,
    };
}

export function normalizeTransactionLogCredentialProtectedPayloadV2(
    payload: TransactionLogCredentialProtectedPayloadV2
): TransactionLogCredentialProtectedPayload {
    const credentialDefinition: TransactionLogCredentialDefinitionEvidence = {
        ...payload.credential_definition,
        credential_field_config: StoredIpxCredentialFieldConfigMapParser.parse(
            payload.credential_definition.credential_field_config
        ),
    };
    return {
        ciphertext_encoding: payload.ciphertext_encoding,
        ciphertext_base64: payload.ciphertext_base64,
        credential_definition: credentialDefinition,
    };
}

export const TransactionLogCredentialProtectedPayloadStorageContract = defineStoredJsonContract({
    contractName: 'TransactionLogCredentialProtectedPayload',
    semanticFieldValues: {
        protected_payload_version: defineStoredJsonSemanticFieldValue<1 | 2>('scalar'),
        protected_payload_kind:
            defineStoredJsonSemanticFieldValue<'CREDENTIAL_DML_SNAPSHOT'>('scalar'),
        ciphertext_encoding: defineStoredJsonSemanticFieldValue<'BASE64'>('scalar'),
        ciphertext_base64: defineStoredJsonSemanticFieldValue<string>('scalar'),
    },
    variants: {
        v1: {
            schemaName: 'TransactionLogCredentialProtectedPayloadV1Schema',
            schema: StoredTransactionLogCredentialProtectedPayloadV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['protected_payload_version'],
                    markerValue: 1,
                },
            ],
            normalize: normalizeTransactionLogCredentialProtectedPayloadV1,
            semanticPaths: {
                protected_payload_version: ['protected_payload_version'],
                protected_payload_kind: ['protected_payload_kind'],
                ciphertext_encoding: ['ciphertext_encoding'],
                ciphertext_base64: ['ciphertext_base64'],
            },
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredTransactionLogCredentialProtectedPayloadV2Schema',
            schema: StoredTransactionLogCredentialProtectedPayloadV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['protected_payload_version'],
                    markerValue: 2,
                },
            ],
            normalize: normalizeTransactionLogCredentialProtectedPayloadV2,
            semanticPaths: {
                protected_payload_version: ['protected_payload_version'],
                protected_payload_kind: ['protected_payload_kind'],
                ciphertext_encoding: ['ciphertext_encoding'],
                ciphertext_base64: ['ciphertext_base64'],
            },
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredTransactionLogCredentialProtectedPayloadParser: z.ZodType<TransactionLogCredentialProtectedPayload> =
    buildStoredJsonValueParser(TransactionLogCredentialProtectedPayloadStorageContract);

export function serializeTransactionLogCredentialProtectedPayloadV2(
    payload: TransactionLogCredentialProtectedPayloadV2
): TransactionLogCredentialProtectedPayloadV2 {
    return TransactionLogCredentialProtectedPayloadV2Schema.parse(payload);
}

export const CredentialHistoricalRevealFailureReasonSchema: z.ZodType<CredentialHistoricalRevealFailureReason> =
    z.enum([
        'ENTRY_NOT_FOUND',
        'NOT_CREDENTIAL_DML',
        'PERSONAL_CREDENTIAL',
        'PROTECTED_EVIDENCE_UNAVAILABLE',
        'HISTORICAL_DEFINITION_UNAVAILABLE',
        'FIELD_UNAVAILABLE',
        'HISTORICAL_KEY_UNAVAILABLE',
        'DECRYPTION_FAILED',
        'AUDIT_STORAGE_FAILED',
    ]);

export const CredentialHistoricalRevealSucceededEventDetailsV1Schema = z
    .object({
        event_details_version: z.literal(1),
        outcome: z.literal('SUCCEEDED'),
        transaction_log_ref: NonEmptyStringSchema.nullable(),
        field_name: NonEmptyStringSchema.nullable(),
    })
    .strict();

export const CredentialHistoricalRevealFailedEventDetailsV1Schema = z
    .object({
        event_details_version: z.literal(1),
        outcome: z.literal('FAILED'),
        transaction_log_ref: NonEmptyStringSchema.nullable(),
        field_name: NonEmptyStringSchema.nullable(),
        failure_reason: CredentialHistoricalRevealFailureReasonSchema,
    })
    .strict();

export const CredentialHistoricalRevealEventDetailsV1Schema: z.ZodType<CredentialHistoricalRevealEventDetailsV1> =
    z.discriminatedUnion('outcome', [
        CredentialHistoricalRevealSucceededEventDetailsV1Schema,
        CredentialHistoricalRevealFailedEventDetailsV1Schema,
    ]);

export const CredentialHistoricalRevealEventDetailsStorageContract = defineStoredJsonContract({
    contractName: 'CredentialHistoricalRevealEventDetails',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'CredentialHistoricalRevealEventDetailsV1Schema',
            schema: CredentialHistoricalRevealEventDetailsV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['event_details_version'],
                    markerValue: 1,
                },
            ],
            normalize: (
                details: CredentialHistoricalRevealEventDetailsV1
            ): CredentialHistoricalRevealEventDetails => details,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredCredentialHistoricalRevealEventDetailsParser: z.ZodType<CredentialHistoricalRevealEventDetails> =
    buildStoredJsonValueParser(CredentialHistoricalRevealEventDetailsStorageContract);

export const EmailAuthenticationAttemptLimitResetFailureCodeSchema: z.ZodType<EmailAuthenticationAttemptLimitResetFailureCode> =
    z.enum(['NOT_AUTHORIZED', 'RESET_FAILED']);

export const EmailAuthenticationAttemptLimitResetSucceededEventDetailsV1Schema = z
    .object({
        event_details_version: z.literal(1),
        outcome: z.literal('SUCCEEDED'),
        target_user_uuid: z.string().uuid(),
    })
    .strict();

export const EmailAuthenticationAttemptLimitResetFailedEventDetailsV1Schema = z
    .object({
        event_details_version: z.literal(1),
        outcome: z.literal('FAILED'),
        target_user_uuid: z.string().uuid(),
        failure_code: EmailAuthenticationAttemptLimitResetFailureCodeSchema,
    })
    .strict();

export const EmailAuthenticationAttemptLimitResetEventDetailsV1Schema: z.ZodType<EmailAuthenticationAttemptLimitResetEventDetailsV1> =
    z.discriminatedUnion('outcome', [
        EmailAuthenticationAttemptLimitResetSucceededEventDetailsV1Schema,
        EmailAuthenticationAttemptLimitResetFailedEventDetailsV1Schema,
    ]);

export const StoredEmailAuthenticationAttemptLimitResetEventDetailsV1Schema: z.ZodType<EmailAuthenticationAttemptLimitResetEventDetailsV1> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain:
                    EmailAuthenticationAttemptLimitResetEventDetailsV1SafeIntegerDomain,
                fields: ['event_details_version'],
            }),
        EmailAuthenticationAttemptLimitResetEventDetailsV1Schema
    );

export const EmailAuthenticationAttemptLimitResetEventDetailsStorageContract =
    defineStoredJsonContract({
        contractName: 'EmailAuthenticationAttemptLimitResetEventDetails',
        semanticFieldValues: {},
        variants: {
            v1: {
                schemaName: 'EmailAuthenticationAttemptLimitResetEventDetailsV1Schema',
                schema: StoredEmailAuthenticationAttemptLimitResetEventDetailsV1Schema,
                guards: [
                    {
                        kind: 'json_marker_equals',
                        markerPath: ['event_details_version'],
                        markerValue: 1,
                    },
                ],
                normalize: (
                    details: EmailAuthenticationAttemptLimitResetEventDetailsV1
                ): EmailAuthenticationAttemptLimitResetEventDetails => details,
                semanticPaths: {},
                unrepresentedSemanticFields: [],
            },
        },
        writerVariant: 'v1',
    });

export const StoredEmailAuthenticationAttemptLimitResetEventDetailsParser: z.ZodType<EmailAuthenticationAttemptLimitResetEventDetails> =
    buildStoredJsonValueParser(EmailAuthenticationAttemptLimitResetEventDetailsStorageContract);

export const RevealHistoricalCredentialFieldRequestSchema: z.ZodType<RevealHistoricalCredentialFieldRequest> =
    z
        .object({
            transaction_log_ref: NonEmptyStringSchema,
            field_name: NonEmptyStringSchema,
        })
        .strict();

export const RevealHistoricalCredentialFieldResponseSchema: z.ZodType<RevealHistoricalCredentialFieldResponse> =
    z
        .object({
            credential_historical_reveal_version: z.literal(1),
            transaction_log_ref: NonEmptyStringSchema,
            field_name: NonEmptyStringSchema,
            field_value: z.string(),
        })
        .strict();

export const CredentialHistoricalRevealFieldDescriptorSchema: z.ZodType<CredentialHistoricalRevealFieldDescriptor> =
    z.object({ field_name: NonEmptyStringSchema, label: NonEmptyStringSchema }).strict();

export const CredentialHistoricalRevealAvailabilitySchema: z.ZodType<CredentialHistoricalRevealAvailability> =
    z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('NOT_APPLICABLE') }).strict(),
        z
            .object({
                kind: z.literal('AVAILABLE'),
                historical_ts: z.string().datetime(),
                credential_definition_uuid: z.string().uuid(),
                fields: z.array(CredentialHistoricalRevealFieldDescriptorSchema),
            })
            .strict(),
        z
            .object({
                kind: z.literal('UNAVAILABLE'),
                reason: z.enum([
                    'LEGACY_PROTECTED_EVIDENCE',
                    'PROTECTED_EVIDENCE_UNAVAILABLE',
                    'HISTORICAL_DEFINITION_UNAVAILABLE',
                ]),
            })
            .strict(),
    ]);

export const TransactionLogPaginationCursorQuerySchema: z.ZodType<string | undefined> = z
    .preprocess((value: unknown): unknown => {
        if (value === undefined || value === null) {
            return undefined;
        }
        const stringParseResult = z.string().safeParse(value);
        if (!stringParseResult.success) {
            return value;
        }
        const trimmed = stringParseResult.data.trim();
        return trimmed.length === 0 ? undefined : trimmed;
    }, z.string().min(1).optional())
    .optional();

export const TransactionLogSortSchema: z.ZodType<TransactionLogSort> = z
    .object({
        field: z.enum(TransactionLogSortFields),
        direction: z.enum(TransactionLogSortDirections),
        json_path: z.array(z.string().trim().min(1)).min(1).optional(),
    })
    .strict()
    .superRefine((sort, context) => {
        if (sort.field === 'row_details' && sort.json_path === undefined) {
            context.addIssue({
                code: 'custom',
                path: ['json_path'],
                message: 'row_details sorting requires json_path',
            });
        }
        if (sort.field !== 'row_details' && sort.json_path !== undefined) {
            context.addIssue({
                code: 'custom',
                path: ['json_path'],
                message: 'json_path is supported only for row_details sorting',
            });
        }
    });

export const TransactionLogSearchRequestSchema: z.ZodType<TransactionLogSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: TransactionLogPaginationCursorQuerySchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: PhysicalJsonStructurePredicatesSchema,
        sort: TransactionLogSortSchema.optional(),
    })
    .strict();

export const ForensicOptionSearchRequestSchema: z.ZodType<ForensicOptionSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: TransactionLogPaginationCursorQuerySchema,
    })
    .strict();

export const ForensicReferenceOptionSchema: z.ZodType<ForensicReferenceOption> = z
    .object({
        value: NonEmptyStringSchema,
        label: NonEmptyStringSchema,
    })
    .strict();

export const ForensicSessionOptionSchema: z.ZodType<ForensicSessionOption> = z
    .object({
        value: NonEmptyStringSchema,
        label: NonEmptyStringSchema,
        first_ts: z.string().datetime(),
        latest_ts: z.string().datetime(),
    })
    .strict();

export const ListForensicReferenceOptionsPageResponseSchema: z.ZodType<ListForensicReferenceOptionsPageResponse> =
    z
        .object({
            options: z.array(ForensicReferenceOptionSchema),
            pagination: OpaqueCursorPaginationSchema,
        })
        .strict();

export const ListForensicSessionOptionsPageResponseSchema: z.ZodType<ListForensicSessionOptionsPageResponse> =
    z
        .object({
            options: z.array(ForensicSessionOptionSchema),
            pagination: OpaqueCursorPaginationSchema,
        })
        .strict();

export const RecentActivityUuidSelectionSchema: z.ZodType<string[] | undefined> = z
    .array(z.string().uuid())
    .min(1)
    .max(100)
    .optional();

export const RecentUserActivityOperationSchema: z.ZodType<RecentUserActivityOperation> = z.enum([
    'MFA_SUCCESS',
    'LOGIN',
    'LOGOUT_SUCCESS',
    'SESSION_EXPIRED',
]);

export const RecentRoleActivityOperationSchema: z.ZodType<RecentRoleActivityOperation> = z.enum([
    'ROLE_ASSIGNMENT_ACTIVATED',
    'IMPERSONATION_STARTED',
    'IMPERSONATION_ENDED',
]);

export const ForensicRoleSchema: z.ZodType<ForensicRoleAssignmentReference['role']> = z.enum(Role);

export const ForensicUserReferenceSchema: z.ZodType<ForensicUserReference> = z
    .object({
        user_uuid: z.string().uuid(),
        primary_email: NonEmptyStringSchema,
    })
    .strict();

export const ForensicOrganizationReferenceSchema: z.ZodType<ForensicOrganizationReference> = z
    .object({
        organization_uuid: z.string().uuid(),
        name: NonEmptyStringSchema,
    })
    .strict();

export const ForensicRoleAssignmentReferenceSchema: z.ZodType<ForensicRoleAssignmentReference> = z
    .object({
        role_assignment_uuid: z.string().uuid(),
        role: ForensicRoleSchema,
        user: ForensicUserReferenceSchema,
        organization: ForensicOrganizationReferenceSchema.nullable(),
    })
    .strict();

export const RecentActivityRoleSchema: z.ZodType<RecentActivityRoleAssignmentReference['role']> =
    ForensicRoleSchema;

export const RecentActivityUserReferenceSchema: z.ZodType<RecentActivityUserReference> =
    ForensicUserReferenceSchema;

export const RecentActivityOrganizationReferenceSchema: z.ZodType<RecentActivityOrganizationReference> =
    ForensicOrganizationReferenceSchema;

export const RecentActivityRoleAssignmentReferenceSchema: z.ZodType<RecentActivityRoleAssignmentReference> =
    ForensicRoleAssignmentReferenceSchema;

interface TransactionLogListItemCommonSchemaShape {
    readonly transaction_log_ref: typeof NonEmptyStringSchema;
    readonly ts: z.ZodString;
    readonly actor: typeof ForensicUserReferenceSchema;
    readonly session_ref: z.ZodNullable<typeof NonEmptyStringSchema>;
    readonly effective_role_assignment: z.ZodNullable<typeof ForensicRoleAssignmentReferenceSchema>;
    readonly ip_address: z.ZodNullable<typeof NonEmptyStringSchema>;
    readonly user_agent: z.ZodNullable<typeof NonEmptyStringSchema>;
    readonly credential_historical_reveal: typeof CredentialHistoricalRevealAvailabilitySchema;
}

const TransactionLogListItemCommonSchemaShape: TransactionLogListItemCommonSchemaShape = {
    transaction_log_ref: NonEmptyStringSchema,
    ts: z.string().datetime(),
    actor: ForensicUserReferenceSchema,
    session_ref: NonEmptyStringSchema.nullable(),
    effective_role_assignment: ForensicRoleAssignmentReferenceSchema.nullable(),
    ip_address: NonEmptyStringSchema.nullable(),
    user_agent: NonEmptyStringSchema.nullable(),
    credential_historical_reveal: CredentialHistoricalRevealAvailabilitySchema,
};

type TransactionLogDmlListItemSchemaShape = Omit<
    TransactionLogListItemCommonSchemaShape,
    'effective_role_assignment'
> & {
    readonly table_name: typeof TransactionLogTableNameSchema;
    readonly row_details: typeof TransactionLogRowDetailsSchema;
    readonly effective_role_assignment: typeof ForensicRoleAssignmentReferenceSchema;
};

const TransactionLogDmlListItemSchemaShape: TransactionLogDmlListItemSchemaShape = {
    ...TransactionLogListItemCommonSchemaShape,
    table_name: TransactionLogTableNameSchema,
    row_details: TransactionLogRowDetailsSchema,
    effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
};

export const TransactionLogInsertListItemSchema: z.ZodType<TransactionLogInsertListItem> = z
    .object({
        ...TransactionLogDmlListItemSchemaShape,
        operation: z.literal('INSERT'),
    })
    .strict();

export const TransactionLogUpdateListItemSchema: z.ZodType<TransactionLogUpdateListItem> = z
    .object({
        ...TransactionLogDmlListItemSchemaShape,
        operation: z.literal('UPDATE'),
    })
    .strict();

export const TransactionLogDeleteListItemSchema: z.ZodType<TransactionLogDeleteListItem> = z
    .object({
        ...TransactionLogDmlListItemSchemaShape,
        operation: z.literal('DELETE'),
    })
    .strict();

export const TransactionLogLoginListItemSchema: z.ZodType<TransactionLogLoginListItem> = z
    .object({
        ...TransactionLogListItemCommonSchemaShape,
        operation: z.literal('LOGIN'),
    })
    .strict();

export const TransactionLogMfaSuccessListItemSchema: z.ZodType<TransactionLogMfaSuccessListItem> = z
    .object({
        ...TransactionLogListItemCommonSchemaShape,
        operation: z.literal('MFA_SUCCESS'),
    })
    .strict();

export const TransactionLogRoleAssignmentActivatedListItemSchema: z.ZodType<TransactionLogRoleAssignmentActivatedListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('ROLE_ASSIGNMENT_ACTIVATED'),
            effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
        })
        .strict();

export const TransactionLogLogoutSuccessListItemSchema: z.ZodType<TransactionLogLogoutSuccessListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('LOGOUT_SUCCESS'),
        })
        .strict();

export const TransactionLogSessionExpiredListItemSchema: z.ZodType<TransactionLogSessionExpiredListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('SESSION_EXPIRED'),
        })
        .strict();

export const TransactionLogImpersonationStartedListItemSchema: z.ZodType<TransactionLogImpersonationStartedListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('IMPERSONATION_STARTED'),
            effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
        })
        .strict();

export const TransactionLogImpersonationEndedListItemSchema: z.ZodType<TransactionLogImpersonationEndedListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('IMPERSONATION_ENDED'),
            effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
        })
        .strict();

export const TransactionLogCredentialHistoricalRevealSucceededListItemSchema: z.ZodType<TransactionLogCredentialHistoricalRevealSucceededListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('CREDENTIAL_HISTORICAL_REVEAL_SUCCEEDED'),
            effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
        })
        .strict();

export const TransactionLogCredentialHistoricalRevealFailedListItemSchema: z.ZodType<TransactionLogCredentialHistoricalRevealFailedListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('CREDENTIAL_HISTORICAL_REVEAL_FAILED'),
            effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
        })
        .strict();

export const TransactionLogEmailAuthenticationAttemptLimitResetSucceededListItemSchema: z.ZodType<TransactionLogEmailAuthenticationAttemptLimitResetSucceededListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('EMAIL_AUTHENTICATION_ATTEMPT_LIMIT_RESET_SUCCEEDED'),
            effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
            event_details: EmailAuthenticationAttemptLimitResetSucceededEventDetailsV1Schema,
        })
        .strict();

export const TransactionLogEmailAuthenticationAttemptLimitResetFailedListItemSchema: z.ZodType<TransactionLogEmailAuthenticationAttemptLimitResetFailedListItem> =
    z
        .object({
            ...TransactionLogListItemCommonSchemaShape,
            operation: z.literal('EMAIL_AUTHENTICATION_ATTEMPT_LIMIT_RESET_FAILED'),
            effective_role_assignment: ForensicRoleAssignmentReferenceSchema,
            event_details: EmailAuthenticationAttemptLimitResetFailedEventDetailsV1Schema,
        })
        .strict();

export const TransactionLogListItemSchema: z.ZodType<TransactionLogListItem> = z.union([
    TransactionLogInsertListItemSchema,
    TransactionLogUpdateListItemSchema,
    TransactionLogDeleteListItemSchema,
    TransactionLogLoginListItemSchema,
    TransactionLogMfaSuccessListItemSchema,
    TransactionLogRoleAssignmentActivatedListItemSchema,
    TransactionLogLogoutSuccessListItemSchema,
    TransactionLogSessionExpiredListItemSchema,
    TransactionLogImpersonationStartedListItemSchema,
    TransactionLogImpersonationEndedListItemSchema,
    TransactionLogCredentialHistoricalRevealSucceededListItemSchema,
    TransactionLogCredentialHistoricalRevealFailedListItemSchema,
    TransactionLogEmailAuthenticationAttemptLimitResetSucceededListItemSchema,
    TransactionLogEmailAuthenticationAttemptLimitResetFailedListItemSchema,
]);

export const ListTransactionLogPageResponseSchema: z.ZodType<ListTransactionLogPageResponse> = z
    .object({
        transaction_log: z.array(TransactionLogListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
    })
    .strict();

function toTransactionLogDisplayItem(row: unknown): TransactionLogDisplayItem {
    const listItem = TransactionLogListItemSchema.safeParse(row);
    if (listItem.success) return listItem.data;
    const reference = z.object({ transaction_log_ref: NonEmptyStringSchema }).safeParse(row);
    const timestamp = z.object({ ts: z.string().datetime() }).safeParse(row);
    return {
        operation: 'UNRECOGNIZED',
        transaction_log_ref: reference.success ? reference.data.transaction_log_ref : null,
        ts: timestamp.success ? timestamp.data.ts : null,
    };
}

/** Display parsing keeps every recognized row when another row is not understood. */
export const ListTransactionLogDisplayPageResponseSchema: z.ZodType<
    ListTransactionLogDisplayPageResponse,
    unknown
> = z
    .object({
        transaction_log: z.array(z.unknown().transform(toTransactionLogDisplayItem)),
        pagination: OpaqueCursorPaginationSchema,
    })
    .strict();

export const CaptureCredentialAuditBaselineResponseSchema: z.ZodType<CaptureCredentialAuditBaselineResponse> =
    z
        .object({
            captured_count: z.number().int().nonnegative(),
        })
        .strict();

export const RecentUserActivityListItemSchema: z.ZodType<RecentUserActivityListItem> = z
    .object({
        activity_ref: NonEmptyStringSchema,
        ts: z.string().datetime(),
        operation: RecentUserActivityOperationSchema,
        session_ref: NonEmptyStringSchema.nullable(),
        user: RecentActivityUserReferenceSchema,
        effective_role_assignment: RecentActivityRoleAssignmentReferenceSchema.nullable(),
        ip_address: NonEmptyStringSchema.nullable(),
        user_agent: NonEmptyStringSchema.nullable(),
    })
    .strict();

export const RecentRoleActivityListItemSchema: z.ZodType<RecentRoleActivityListItem> = z
    .object({
        activity_ref: NonEmptyStringSchema,
        ts: z.string().datetime(),
        operation: RecentRoleActivityOperationSchema,
        session_ref: NonEmptyStringSchema.nullable(),
        actor: RecentActivityUserReferenceSchema,
        effective_role_assignment: RecentActivityRoleAssignmentReferenceSchema,
        ip_address: NonEmptyStringSchema.nullable(),
        user_agent: NonEmptyStringSchema.nullable(),
    })
    .strict();

export const RecentUserActivitySearchRequestSchema: z.ZodType<RecentUserActivitySearchRequest> = z
    .object({
        user_uuids: RecentActivityUuidSelectionSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: TransactionLogPaginationCursorQuerySchema,
    })
    .strict();

export const RecentRoleActivitySearchRequestSchema: z.ZodType<RecentRoleActivitySearchRequest> = z
    .object({
        user_uuids: RecentActivityUuidSelectionSchema,
        organization_uuids: RecentActivityUuidSelectionSchema,
        roles: z.array(RecentActivityRoleSchema).min(1).max(20).optional(),
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: TransactionLogPaginationCursorQuerySchema,
    })
    .strict();

export const ListRecentUserActivityPageResponseSchema: z.ZodType<ListRecentUserActivityPageResponse> =
    z
        .object({
            activity: z.array(RecentUserActivityListItemSchema),
            pagination: OpaqueCursorPaginationSchema,
        })
        .strict();

export const ListRecentRoleActivityPageResponseSchema: z.ZodType<ListRecentRoleActivityPageResponse> =
    z
        .object({
            activity: z.array(RecentRoleActivityListItemSchema),
            pagination: OpaqueCursorPaginationSchema,
        })
        .strict();
